import { DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  afterNextRender,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  KcDialogComponent,
  KcIconComponent,
  KcInputComponent,
} from '@kaspacom/ui-kit';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { MarketDiscoveryManager } from '../../managers/market-discovery.manager';
import { TokenLogoComponent } from '../token-logo/token-logo.component';
import { Kcc20MarketSummary } from '../../services/trading/kcc20-trading-api.service';
import { shortId, tokenIdentityLabel } from '../../shared/kcc20-formatters';

@Component({
  selector: 'kcc20-token-search-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    KcDialogComponent,
    KcInputComponent,
    KcIconComponent,
    TokenLogoComponent,
  ],
  templateUrl: './token-search-modal.component.html',
  styleUrl: './token-search-modal.component.scss',
})
export class TokenSearchModalComponent implements OnInit {
  private readonly dialogRef = inject(DialogRef<void>);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly marketDiscovery = inject(MarketDiscoveryManager);
  private readonly destroyRef = inject(DestroyRef);

  private searchSeq = 0;
  private loadingCount = 0;

  scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');

  searchForm = this.fb.group({ query: [''] });

  searchResults = signal<Kcc20MarketSummary[]>([]);
  loading = signal(false);
  currentQuery = '';

  constructor() {
    // Dialog content is projected via ng-template, so wait until it paints
    // before focusing — otherwise the user has to click the field to type.
    afterNextRender(() => {
      this.focusSearchInput();
    });
  }

  ngOnInit(): void {
    void this.runSearch('');

    this.searchForm
      .get('query')!
      .valueChanges.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((query) => {
        void this.runSearch(query ?? '');
      });
  }

  private focusSearchInput(): void {
    const findInput = (): HTMLInputElement | null =>
      this.host.nativeElement.querySelector('input');

    const input = findInput();
    if (input) {
      input.focus();
      return;
    }
    // kc-dialog stamps contentTemplate one tick later than afterNextRender.
    setTimeout(() => findInput()?.focus());
  }

  private async runSearch(query: string): Promise<void> {
    const seq = ++this.searchSeq;
    this.currentQuery = query;
    const container = this.scrollContainer()?.nativeElement;
    if (container) container.scrollTop = 0;

    const trimmed = query.trim();
    this.beginLoad();
    try {
      const page = await firstValueFrom(
        this.marketDiscovery.loadDiscovery({
          ...(trimmed ? { search: trimmed } : {}),
          limit: 30,
          offset: 0,
          sortBy: 'volume24hSompi',
          sortDirection: 'desc',
        }),
      );
      if (seq !== this.searchSeq) return;
      this.searchResults.set(page?.items ?? []);
    } catch {
      if (seq === this.searchSeq) this.searchResults.set([]);
    } finally {
      this.endLoad();
    }
  }

  private beginLoad(): void {
    this.loadingCount++;
    this.loading.set(true);
  }

  private endLoad(): void {
    this.loadingCount = Math.max(0, this.loadingCount - 1);
    if (this.loadingCount === 0) this.loading.set(false);
  }

  openToken(market: Kcc20MarketSummary): void {
    // The token page keys everything off the covenant id; tokenIdHex is only a
    // fallback for market rows that carry no metadata.
    const covenantId = market.metadata?.covenantId ?? market.tokenIdHex;
    void this.router.navigate(['/tokens', covenantId]);
    this.closeModal();
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  labelFor(market: Kcc20MarketSummary): string {
    return tokenIdentityLabel(market.metadata, market.tokenIdHex);
  }

  nameFor(market: Kcc20MarketSummary): string | undefined {
    return market.metadata?.name?.trim() || undefined;
  }

  tickerFor(market: Kcc20MarketSummary): string | undefined {
    return market.metadata?.ticker?.trim() || undefined;
  }

  shortTokenId(tokenIdHex: string): string {
    return shortId(tokenIdHex);
  }

  trackMarket(_: number, market: Kcc20MarketSummary): string {
    return market.tokenIdHex;
  }
}
